import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Lock, CreditCard, LogOut, Trash2, ArrowRight, Loader2, Mail, FileDown } from 'lucide-react';
import { TeamInviteBox } from './TeamInviteBox';
import { TeamMemberList, TeamStatusBanner } from './TeamMemberList';
import { ProjectForm } from './ProjectForm';
import { PriceBreakdown } from './PriceBreakdown';
import { GuestTeamModal } from './GuestTeamModal';
import { JoinRequests, TeamFinderBoard } from './TeamFinderBoard';
import { TeamChat } from './TeamChat';
import { ClaimSpotBox } from './ClaimSpotBox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const istToday = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
const projectOpensOn = (event) => (/^\d{4}-\d{2}-\d{2}/.test(event.event_date || '') ? event.event_date.slice(0, 10) : '');
const projectOpen = (event) => !projectOpensOn(event) || istToday() >= projectOpensOn(event);

const API = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `INR ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const startCashfree = async (sessionId, returnUrl) => {
  const { load } = await import('@cashfreepayments/cashfree-js');
  const cashfree = await load({ mode: 'production' });
  cashfree.checkout({ paymentSessionId: sessionId, returnUrl });
};

const ConfirmDialog = ({ state, onClose }) => (
  <AlertDialog open={!!state} onOpenChange={(o) => !o && onClose()}>
    <AlertDialogContent data-testid="team-confirm-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="text-[#0A0A0A]">{state?.title}</AlertDialogTitle>
        <AlertDialogDescription className="text-[#52525B]">{state?.text}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="text-[#0A0A0A] border-[#E4E4E7]" data-testid="team-confirm-cancel">Go back</AlertDialogCancel>
        <AlertDialogAction className="bg-[#002FA7] hover:bg-[#00227A] text-white" data-testid="team-confirm-ok" onClick={() => { state.onOk(); onClose(); }}>{state?.ok || 'Confirm'}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const CouponInput = ({ value, onChange, testId }) => (
  <input value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} placeholder="Coupon code"
    className="px-3 py-2.5 border border-[#E4E4E7] rounded text-sm uppercase w-44 placeholder:normal-case" data-testid={testId} />
);

const PendingInvites = ({ invites, onAct, busy }) => (
  <div className="space-y-2" data-testid="event-pending-invites">
    {invites.map((inv) => (
      <div key={inv.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#002FA7]/30 bg-[#002FA7]/5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-1.5"><Mail size={14} className="text-[#002FA7]" /> Invite to join <b>{inv.team_name}</b></p>
          <p className="text-xs text-[#52525B]">from {inv.invited_by_name || 'a teammate'}</p>
        </div>
        <div className="flex gap-2">
          <button disabled={busy} onClick={() => onAct('accept', inv.id)} className="px-3 py-1.5 bg-[#002FA7] text-white rounded text-xs font-bold disabled:opacity-50" data-testid={`accept-invite-${inv.id}`}>Accept</button>
          <button disabled={busy} onClick={() => onAct('decline', inv.id)} className="px-3 py-1.5 border border-[#E4E4E7] rounded text-xs font-semibold text-[#52525B]" data-testid={`decline-invite-${inv.id}`}>Decline</button>
        </div>
      </div>
    ))}
  </div>
);

const PricingLine = ({ pricing, isPaid, due, teamPaid }) => {
  if (!isPaid) return <p className="text-sm text-[#52525B]">Free event — no payment needed.</p>;
  if (pricing.payment_mode !== 'per_member' && (due || teamPaid)) return (
    <p className="text-sm text-[#52525B]" data-testid="team-pricing">
      {due ? <>Due now: <b className="text-[#0A0A0A]">{inr(due.amount)}</b> for {due.count} unpaid member{due.count === 1 ? '' : 's'}{teamPaid ? ' (already-paid members are not charged again)' : ''}.</>
        : <>Team fee paid — everyone's spot is covered.</>}
    </p>
  );
  const perMember = pricing.payment_mode === 'per_member';
  return (
    <div className="text-sm text-[#52525B]" data-testid="team-pricing">
      {perMember
        ? <>Each member pays <b className="text-[#0A0A0A]">{inr(pricing.unit_price)}</b> individually.</>
        : <>Leader pays <b className="text-[#0A0A0A]">{inr(pricing.payable ?? pricing.total)}</b> for the team
          {pricing.fee_type === 'per_member' ? ` (${inr(pricing.unit_price)} × ${pricing.member_count})`
            : pricing.fee_type === 'hybrid' ? ` (${inr(pricing.base_fee)} team fee + ${inr(pricing.unit_price)} × ${pricing.member_count})` : ' (flat team fee)'}.</>}
    </div>
  );
};

export const EventTeamPanel = ({ event, onTeamChange }) => {
  const token = localStorage.getItem('codearena_token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [busy, setBusy] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [coupon, setCoupon] = useState('');
  const [memberQuote, setMemberQuote] = useState(null);
  const [guestOpen, setGuestOpen] = useState(false);
  useEffect(() => {
    if (!event.is_paid || event.team_payment_mode !== 'per_member') return;
    axios.get(`${API}/api/public/events/${event.id}/price-quote`).then((r) => setMemberQuote(r.data)).catch(() => {});
  }, [event.id, event.is_paid, event.team_payment_mode]);
  const [confirmState, setConfirmState] = useState(null);
  const [showProject, setShowProject] = useState(false);
  const ask = (title, text, onOk, ok) => setConfirmState({ title, text, onOk, ok });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/event-teams/${event.id}/my-team`, { headers });
      setData(res.data);
      onTeamChange?.(!!res.data.team);
    } catch (err) {
      if (err.response?.status === 401) localStorage.removeItem('codearena_token');
      else toast.error(err.response?.data?.detail || 'Could not load team');
    } finally { setLoading(false); }
  }, [event.id, token]);

  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    const orderId = p.get('team_order_id');
    const teamId = p.get('team_id');
    if (orderId && teamId && !token && p.get('guest')) {
      axios.post(`${API}/api/public/guest-teams/${teamId}/verify-payment?order_id=${orderId}`)
        .then((r) => { r.data.status === 'success' ? toast.success('Payment received — your team is confirmed! Tickets and account activation links are in your inbox.') : toast.info(r.data.message || 'Payment is being processed'); })
        .catch(() => toast.error('Could not verify payment'))
        .finally(() => window.history.replaceState({}, '', `/events/${event.id}`));
    }
    if (orderId && teamId && token) {
      axios.post(`${API}/api/event-teams/teams/${teamId}/verify-payment?order_id=${orderId}`, {}, { headers })
        .then((r) => { r.data.status === 'success' ? toast.success('Payment received — team confirmed!') : toast.info('Payment is being processed'); load(); })
        .catch(() => toast.error('Could not verify payment'))
        .finally(() => window.history.replaceState({}, '', `/events/${event.id}`));
    }
  }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (okMsg) toast.success(r?.data?.message || okMsg); await load(); return r; }
    catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong'); return null; }
    finally { setBusy(false); }
  };

  const team = data?.team;
  const cfg = data?.config;
  const me = data?.my_member;
  const isLeader = me?.role === 'leader';
  const myReg = data?.my_registration;

  const createTeam = () => teamName.trim().length >= 2
    ? act(() => axios.post(`${API}/api/event-teams/${event.id}/teams`, { name: teamName.trim() }, { headers }), 'Team created')
    : toast.error('Team name must be at least 2 characters');
  const inviteAct = (kind, id) => act(() => axios.post(`${API}/api/event-teams/invites/${kind}`, { invite_id: id }, { headers }), kind === 'accept' ? 'Joined team' : 'Invite declined');
  const finalize = () => ask('Lock the team?', 'Membership becomes final and pending invites are revoked.',
    () => act(() => axios.post(`${API}/api/event-teams/teams/${team.id}/finalize`, {}, { headers }), 'Team locked'), 'Lock team');
  const leave = () => ask('Leave this team?', 'You can join or create another team afterwards.',
    () => act(() => axios.delete(`${API}/api/event-teams/teams/${team.id}/members/${me.id}`, { headers }), 'Left team'), 'Leave');
  const cancel = () => ask('Cancel the team?', 'All members will be released and the team name freed.',
    () => act(() => axios.post(`${API}/api/event-teams/teams/${team.id}/cancel`, {}, { headers }), 'Team cancelled'), 'Cancel team');
  const remove = (m) => ask(`Remove ${m.name}?`, 'They will lose their spot and ticket for this event.',
    () => act(() => axios.delete(`${API}/api/event-teams/teams/${team.id}/members/${m.id}`, { headers }), 'Member removed'), 'Remove');
  const revoke = (inv) => act(() => axios.delete(`${API}/api/event-teams/teams/${team.id}/invites/${inv.id}`, { headers }), 'Invite revoked');

  const payTeam = async () => {
    const r = await act(() => axios.post(`${API}/api/event-teams/teams/${team.id}/create-payment?coupon_code=${encodeURIComponent(coupon.trim())}`, {}, { headers }));
    if (r?.data?.payment_session_id) {
      await startCashfree(r.data.payment_session_id, `${window.location.origin}/events/${event.id}?team_order_id=${r.data.order_id}&team_id=${team.id}&status=success`);
    } else if (r?.data?.confirmed) toast.success('Team confirmed!');
  };
  const payMyShare = async () => {
    const q = coupon.trim() ? `&coupon_code=${encodeURIComponent(coupon.trim())}` : '';
    const r = await act(() => axios.post(`${API}/api/public/events/${event.id}/create-payment?registration_id=${myReg.id}${q}`));
    if (r?.data?.payment_session_id) {
      await startCashfree(r.data.payment_session_id, `${window.location.origin}/events/${event.id}?order_id=${r.data.order_id}&status=success`);
    } else if (r) toast.success('Your share is paid');
  };
  const downloadPack = async () => {
    try {
      const res = await axios.get(`${API}/api/event-teams/teams/${team.id}/ticket-pack`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${team.name.replace(/[^\w-]/g, '') || 'team'}-ticket-pack.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Ticket pack downloaded');
    } catch { toast.error('Could not download the ticket pack'); }
  };

  return (
    <section className="bg-white rounded-lg border-2 border-[#0A0A0A] p-6 md:p-8" id="team-registration" data-testid="event-team-panel">
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div>
          <p className="text-xs font-bold text-[#002FA7] uppercase tracking-[0.12em]">Team registration</p>
          <h2 className="text-xl font-extrabold text-[#0A0A0A] tracking-tight mt-1">{team ? team.name : 'Form your team'}</h2>
        </div>
        {cfg && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#52525B] bg-[#F4F4F5] px-3 py-1.5 rounded"><Users size={13} /> {cfg.min_size}–{cfg.max_size} members</span>}
      </div>

      {!token ? (
        <div className="text-center py-6">
          <p className="text-sm text-[#52525B] mb-4">Register your team directly — no account needed — or log in to your EvalentX Arena account.</p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            {!event.under_review && (
              <button type="button" onClick={() => setGuestOpen(true)} className="inline-flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white px-6 py-3 rounded font-semibold text-sm" data-testid="guest-team-register-btn">
                Register team without account <ArrowRight size={16} />
              </button>
            )}
            <Link to={`/codearena/auth?redirect=${encodeURIComponent(`/events/${event.id}`)}`} className="inline-flex items-center justify-center gap-2 border border-[#E4E4E7] text-[#0A0A0A] hover:bg-[#F4F4F5] px-6 py-3 rounded font-semibold text-sm" data-testid="team-login-btn">
              Log in to continue
            </Link>
          </div>
          {guestOpen && <GuestTeamModal event={event} onClose={() => setGuestOpen(false)} />}
        </div>
      ) : loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#A1A1AA]" /></div>
      ) : !data ? null : !team ? (
        <div className="space-y-5">
          {data.claimable && (
            <ClaimSpotBox onLinked={load} text={<>A spot in team <b>{data.claimable.team_name}</b> is reserved for your email by {data.claimable.leader_name || 'the team leader'}. Verify your email to access it.</>} />
          )}
          {data.my_invites?.length > 0 && <PendingInvites invites={data.my_invites} onAct={inviteAct} busy={busy} />}
          {data.deadline_passed ? <p className="text-sm text-red-600 font-medium">Registration deadline has passed.</p> : (
            <div>
              <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-2">Create a new team (you become the leader)</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input value={teamName} onChange={(e) => setTeamName(e.target.value)} maxLength={60} placeholder="e.g. Byte Squad"
                  className="flex-1 px-3 py-2.5 border border-[#E4E4E7] rounded text-sm focus:outline-none focus:border-[#002FA7]" data-testid="team-name-input" />
                <button onClick={createTeam} disabled={busy} className="px-5 py-2.5 bg-[#002FA7] hover:bg-[#00227A] text-white rounded text-sm font-semibold disabled:opacity-50" data-testid="create-team-btn">Create team</button>
              </div>
            </div>
          )}
          <PricingLine pricing={data.pricing} isPaid={event.is_paid} />
        </div>
      ) : (
        <div className="space-y-5">
          <TeamStatusBanner team={team} cfg={cfg} />
          <TeamMemberList team={team} eventId={event.id} myUserId={me?.user_id} isLeader={isLeader} onRemove={remove} onRevoke={revoke} />
          {team.status !== 'cancelled' && <TeamChat teamId={team.id} myUserId={me?.user_id} isLeader={isLeader} headers={headers} />}
          {isLeader && team.status === 'forming' && <JoinRequests teamId={team.id} headers={headers} onChange={load} />}
          {isLeader && team.status !== 'cancelled' && team.member_count < cfg.max_size && (
            <TeamInviteBox eventId={event.id} teamId={team.id} token={token} onInvited={load} canInvite={team.status === 'forming'} />
          )}
          {['locked', 'confirmed'].includes(team.status) && !projectOpen(event) && (
            <p className="rounded-lg border border-dashed border-[#E4E4E7] p-3 text-xs text-[#52525B]" data-testid="project-opens-note">
              Project details are submitted on the hackathon day ({projectOpensOn(event)}). Nothing to fill in now.
            </p>
          )}
          {['locked', 'confirmed'].includes(team.status) && projectOpen(event) && (
            <div className="rounded-lg border border-[#E4E4E7] p-4" data-testid="team-project-section">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-[#0A0A0A]">Your project</p>
                  <p className="text-xs text-[#52525B]">{team.project ? team.project.title : 'Submit what you built — winners are showcased on this page.'}</p>
                </div>
                <button onClick={() => setShowProject(!showProject)} className="text-xs font-bold text-[#002FA7]" data-testid="toggle-project-form-btn">{showProject ? 'Close' : team.project ? 'Edit project' : 'Submit project'}</button>
              </div>
              {showProject && (
                <div className="mt-4">
                  <ProjectForm initial={team.project} headers={headers} saveUrl={`${API}/api/event-teams/teams/${team.id}/project`}
                    uploadUrl={`${API}/api/event-teams/teams/${team.id}/project/image`} onSaved={() => { setShowProject(false); load(); }} />
                </div>
              )}
            </div>
          )}
          <PricingLine pricing={data.pricing} isPaid={event.is_paid} due={isLeader ? data.payable : null} teamPaid={team.payment_status === 'paid'} />
          {isLeader && cfg.payment_mode === 'leader' && ['locked', 'confirmed'].includes(team.status) && data.payable?.price_breakdown?.charges?.length > 0 && (
            <PriceBreakdown b={data.payable.price_breakdown} baseLabel={`Team fee (${data.payable.count} member${data.payable.count === 1 ? '' : 's'})`} testId="team-price-breakdown" />
          )}
          {cfg.payment_mode === 'per_member' && event.is_paid && myReg?.payment_status === 'pending' && memberQuote?.charges?.length > 0 && (
            <PriceBreakdown b={memberQuote} baseLabel="Your share" testId="member-price-breakdown" />
          )}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E4E4E7]">
            {isLeader && team.status === 'forming' && (
              <button onClick={finalize} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#0A0A0A] text-white rounded text-sm font-semibold disabled:opacity-50" data-testid="finalize-team-btn"><Lock size={14} /> Lock team</button>
            )}
            {isLeader && data.payable && cfg.payment_mode === 'leader' && ['locked', 'confirmed'].includes(team.status) && (
              <>
                <CouponInput value={coupon} onChange={setCoupon} testId="team-coupon-input" />
                <button onClick={payTeam} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#002FA7] text-white rounded text-sm font-semibold disabled:opacity-50" data-testid="pay-team-btn"><CreditCard size={14} /> Pay {inr(data.payable.amount)} for {data.payable.count} member{data.payable.count === 1 ? '' : 's'}</button>
              </>
            )}
            {cfg.payment_mode === 'per_member' && event.is_paid && myReg?.payment_status === 'pending' && team.status !== 'cancelled' && (
              <>
              <CouponInput value={coupon} onChange={setCoupon} testId="member-coupon-input" />
              <button onClick={payMyShare} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#002FA7] text-white rounded text-sm font-semibold disabled:opacity-50" data-testid="pay-my-share-btn"><CreditCard size={14} /> Pay my share</button>
              </>
            )}
            {isLeader && team.members?.some((m) => m.ticket_status === 'active') && (
              <button onClick={downloadPack} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-[#E4E4E7] rounded text-sm font-semibold text-[#0A0A0A] hover:bg-[#F4F4F5] transition-colors disabled:opacity-50" data-testid="download-ticket-pack-btn"><FileDown size={14} /> Group ticket pack (PDF)</button>
            )}
            {!isLeader && team.status === 'forming' && (
              <button onClick={leave} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-[#E4E4E7] rounded text-sm font-semibold text-[#52525B]" data-testid="leave-team-btn"><LogOut size={14} /> Leave team</button>
            )}
            {isLeader && team.status === 'forming' && (
              <button onClick={cancel} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded text-sm font-semibold ml-auto" data-testid="cancel-team-btn"><Trash2 size={14} /> Cancel team</button>
            )}
          </div>
        </div>
      )}
      {token && data && !event.under_review && !data.deadline_passed && (
        <div className="mt-6"><TeamFinderBoard eventId={event.id} team={team} isLeader={isLeader} headers={headers} onChange={load} /></div>
      )}
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </section>
  );
};
